import React, { useCallback, useMemo, useReducer } from 'react';
import { get } from 'lodash';
import axios from 'axios';
import { ERR_MSG, SUCCESS_MSG } from '../globalConstants';
import Alert from './alert';

interface LoaderState {
  data: any;
  error: any;
  loading: boolean;
}

const dataLoaderReducer = (
  state: LoaderState,
  [action, body]: [string, any],
): LoaderState => {
  switch (action) {
    case 'loaded':
      return {
        ...state,
        data: body,
        loading: false,
      };
    case 'error':
      return {
        ...state,
        error: body,
        loading: false,
      };
    case 'loading':
      // A new attempt clears the previous failure, otherwise a stale error
      // (e.g. a 403 access-denied panel) would stick around after a refetch
      // succeeds -- containers refetch on route param change without remounting.
      return {
        ...state,
        loading: body,
        error: null,
      };
    default:
      return state;
  }
};

const initialDataLoader = (initialState: any): LoaderState => ({
  data: null,
  error: null,
  loading: false,
  ...(typeof initialState === 'function' ? initialState() : initialState),
});

export const useLoaderState = (initialState: any = null) => {
  const [state, dispatch] = useReducer(
    dataLoaderReducer,
    initialState,
    initialDataLoader,
  );
  const [onLoad, onError, onLoading] = useMemo(() => {
    return [
      (data) => dispatch(['loaded', data]),
      (error) => dispatch(['error', error]),
      (loading = true) => dispatch(['loading', loading]),
    ];
  }, []);
  return [state.data, state.loading, state.error, onLoad, onError, onLoading];
};

/**
 * Store a failed request and toast it where a toast is warranted. Shared with
 * usePaginatedData so both hooks read the API's errors the same way.
 *
 * The body's `code` -- not the HTTP status -- is what identifies these:
 * PlanExceedException also answers 403, but sends no `code`, so it falls
 * through to the generic message the caller supplied.
 */
export const applyApiError = (
  realError: any,
  store: (error: any) => void,
  errorMsg?: any,
) => {
  const { data: errResponse } = realError.response || {};
  const bodyCode = errResponse?.code as number | undefined;
  if (bodyCode === 403 || bodyCode === 405) {
    if (bodyCode === 405) {
      Alert.error(errResponse.error);
    }
    store(errResponse);
    return;
  }

  const errorToStore = errorMsg || realError;
  if (typeof errorMsg === 'string') {
    Alert.error(errorToStore);
  } else if (!errorMsg) {
    Alert.error(ERR_MSG);
  }
  store(errorToStore);
};

/**
 * True when the API refused the record itself, rather than failing to load it.
 * Keeps the body-code protocol inside this module instead of in every caller.
 */
export const isAccessDenied = (error: any): boolean => error?.code === 403;

export function useData(
  endpoint: string,
  dataOptions: Record<string, any> = {},
) {
  const [data, loading, error, onLoad, onErrorInner, onLoading] =
    useLoaderState({
      loading: dataOptions.initialLoading,
      data: dataOptions.initialData || [],
    });
  const onError = useCallback(
    (realError: any) =>
      applyApiError(realError, onErrorInner, dataOptions.errorMsg),
    [dataOptions.errorMsg, onErrorInner],
  );
  const fetchData = useCallback(
    async (...rest) => {
      onLoading();
      try {
        const axiosResponse = await axios[dataOptions.reqType || 'get'](
          `${endpoint}`,
          ...rest,
        );

        if (axiosResponse.status === 200) {
          const { data: resp } = axiosResponse;

          const responseData = resp.current_page
            ? resp
            : get(
                axiosResponse,
                'data.data',
                get(axiosResponse, 'data', axiosResponse),
              );

          onLoad(responseData);
        } else {
          onError(axiosResponse);
        }
      } catch (err) {
        onError(err);
      }
    },
    [endpoint, onLoad, onError, onLoading, dataOptions.reqType],
  );
  return [data, fetchData, loading, error];
}

export function withData({
  endpoint,
  propsKey = 'data',
}: {
  endpoint: string;
  propsKey?: string;
}) {
  return (WrappedComponent: React.ComponentType<any>) => {
    return function WithData(props: Record<string, any>) {
      const [data, fetchData, loading] = useData(endpoint);
      const childProps = {
        fetch: fetchData,
        ...props,
        loading,
        [propsKey]: data,
      };
      return <WrappedComponent {...childProps} />;
    };
  };
}

export const successRefetch = (
  fetch: () => void,
  msg: string | null = null,
) => {
  if (msg) {
    Alert.success(msg);
  } else {
    Alert.success(SUCCESS_MSG);
  }

  fetch();
};
